import { useCallback } from "react";
import { useStickyState } from "./useStickyState";
import type { Product } from "@/components/settings/ProductsSettings";

/**
 * A product line item attached to a deal.
 * Frontend-only persistence (sticky state) — backend wiring lands later.
 */
export interface DealLineItem {
  productId: string;
  /** Snapshot of the product name at the time it was added, for display. */
  name: string;
  quantity: number;
  /** Price per unit (defaults to listPrice when added). */
  price: number;
}

const STORAGE_KEY = "deal-products-v1";

/**
 * Workspace-wide map of dealId -> line items.
 * Single shared sticky state so the table, kanban, AddDealDialog, and detail
 * views all read/write the same source of truth in preview mode.
 */
export function useDealProductsMap() {
  const [map, setMap] = useStickyState<Record<string, DealLineItem[]>>(STORAGE_KEY, {});

  const setForDeal = useCallback(
    (dealId: string, items: DealLineItem[]) => {
      setMap(prev => {
        const next = { ...prev };
        if (items.length === 0) delete next[dealId];
        else next[dealId] = items;
        return next;
      });
    },
    [setMap]
  );

  return { map, setForDeal };
}

/** Convenience helper for a single deal. */
export function useDealProducts(dealId: string | null | undefined) {
  const { map, setForDeal } = useDealProductsMap();
  const items = (dealId && map[dealId]) || [];
  const setItems = useCallback(
    (next: DealLineItem[]) => {
      if (dealId) setForDeal(dealId, next);
    },
    [dealId, setForDeal]
  );
  return { items, setItems };
}

/** Build a default line item from a Product catalog entry. */
export function lineItemFromProduct(p: Product, quantity = 1): DealLineItem {
  return {
    productId: p.id,
    name: p.name,
    quantity,
    price: p.listPrice ?? 0,
  };
}

export function lineItemsTotal(items: DealLineItem[]): number {
  return items.reduce((sum, li) => sum + li.price * li.quantity, 0);
}